import { Inject, inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APIResult } from '../models/APIResult.model';
import { AuthStateService, Show } from '../app/auth/auth-state.service';
import { API_BASE_URL } from '../app/tokens/api.tokens';

/**
 * Thin HTTP wrapper.
 *
 * **Auth headers are NOT attached here.** `AuthInterceptor` is the single
 * source of truth for the `Authorization` and `Show` headers — see
 * `src/app/auth/auth-interceptor.ts`. The previous version of this file
 * also built those headers per-request via a `buildHeaders()` helper that
 * caught its own errors and `return {}`-ed on failure, which fail-open'd
 * an unauthenticated request whenever the credential lookup threw. The
 * helper has been removed (P0.6).
 */
@Injectable({
    providedIn: 'root'
})
export class ApiService {

    // TODO: Maak hier een class van
    private readonly errorMessagesMap: { [key: number]: string } = {
        0: '0: Kan geen verbinding maken met de server.',
        404: '404: De gevraagde bron kon niet worden gevonden.'
        // Voeg hier meer statuscodes en foutmeldingen toe indien nodig
    };

    private readonly http: HttpClient = inject(HttpClient);
    private readonly authStateService: AuthStateService = inject(AuthStateService);

    constructor(
        @Inject(API_BASE_URL) private readonly apiBaseUrl: string
    ) {}

    /**
     * Builds a full URL from the injected base URL and the given endpoint.
     *
     * @param endpoint The API endpoint (e.g., 'users', 'tasks/123')
     * @returns The full URL (e.g., 'https://api.example.com/users')
     */
    public buildUrl(endpoint: string): string {
        return `${this.apiBaseUrl}/${endpoint}`;
    }

    public async getAll<T>(endpoint: string): Promise<T[]> {
        const url = this.buildUrl(endpoint);
        return (
            await this.handleRequest<APIResult<T>>(
                this.http.get<APIResult<T>>(url)
            )
        ).data;
    }

    public async getAppVersion(): Promise<string> {
        const url = this.buildUrl(`version`);
        const result = await this.handleRequest<APIResult<string>>(
            this.http.get<APIResult<string>>(url)
        );
        return result.data[0];
    }

    public async getById<T>(endpoint: string, id: number): Promise<T> {
        const url = this.buildUrl(`${ endpoint }/${ id }`);
        const result = await this.handleRequest<APIResult<T>>(
            this.http.get<APIResult<T>>(url)
        );

        // If data is an array, return the first item, else return data directly
        return Array.isArray(result.data) ? result.data[0] : result.data;

    }

    public async post<T>(endpoint: string, data: any): Promise<T[]> {
        try {
            const url = this.buildUrl(endpoint);
            return (
                await this.handleRequest<APIResult<T>>(
                    this.http.post<APIResult<T>>(url, data)
                )
            ).data;
        } catch (error) {
            throw error;
        }
    }

    public async postRequest(endpoint: string, data: any): Promise<any> {
        try {
            const url = this.buildUrl(endpoint);
            const response = await this.http.post(url, data).toPromise();
            return response;

        } catch (error) {
            throw error;
        }
    }

    public async put<T>(endpoint: string, data: any): Promise<any> {
        try {
            const url = this.buildUrl(endpoint);
            return (
                await this.handleRequest<APIResult<T>>(
                    this.http.put<APIResult<T>>(url, data)
                )
            ).data;
        } catch (error) {
            console.log('%c[ApiService] put() - error', 'background-color: red; color: white; padding: 8px', error);
            throw error;
        }
    }

    public async getSpecific<T>(endpoint: string, start: number, amount: number) {

        const currentShowId = this.authStateService.activeShowId;

        const url =            this.buildUrl(endpoint) + '?start=' + start + '&amount=' + amount + '&showId=' + currentShowId;
        return (
            await this.handleRequest<APIResult<T>>(
                this.http.get<APIResult<T>>(url)
            )
        ).data;
    }

    async get<T>(endpoint: string) {
        try {
            const url = this.buildUrl(endpoint);

            return (
                await this.handleRequest<any>(
                    this.http.get<any>(url)
                )
            ).data;
        } catch (error) {
            console.log('%c[ApiService] get() - error', 'background-color: red; color: white; padding: 8px', error);
            throw error;
        }
    }

    // THis should be the new standard, without the data
    async getWithoutData(endpoint: string) {
        try {
            const url = this.buildUrl(endpoint);

            return await this.http.get<any>(url).toPromise();
        } catch (error) {
            throw error;
        }
    }

    /**
     * Performs a DELETE request to the specified endpoint.
     */
    public async delete(endpoint: string): Promise<void> {
        try {
            const url = this.buildUrl(endpoint);
            await this.http.delete(url).toPromise();
        } catch (error) {
            throw error;
        }
    }

    /**
     * Performs a PATCH request to the specified endpoint with the given data.
     */
    public async patch<T>(endpoint: string, data: any): Promise<T> {
        try {
            const url = this.buildUrl(endpoint);
            const result = await this.handleRequest<APIResult<T>>(
                this.http.patch<APIResult<T>>(url, data)
            );
            return Array.isArray(result.data) ? result.data[0] : result.data;
        } catch (error) {
            throw error;
        }
    }

    private async handleRequest<T>(request: Observable<T>): Promise<T> {

        try {
            return await request.toPromise();
        } catch (error) {
            if (error instanceof HttpErrorResponse) {
                const status = error.status;
                const errorMessage =                    this.errorMessagesMap[status] ||                    'Er is een onverwachte fout opgetreden.';

                console.error('[ApiService] handleRequest failed:', errorMessage);
                throw error;
            }

            throw new Error('An error occurred while communicating with the API.');
        }
    }

    // ============ Show Management Methods ============

    /**
     * Gets all shows available to the current user.
     */
    public async getMyShows(): Promise<Show[]> {
        try {
            const result = await this.get<Show[]>('users/me/shows');
            return result || [];
        } catch (error) {
            console.error('[ApiService] getMyShows() error:', error);
            throw error;
        }
    }

    /**
     * Refetches the current user's shows and pushes the result through
     * {@link AuthStateService.setAvailableShows}, which in turn drives the
     * {@link AuthStateService.hasMultipleShows} computed signal. Use this
     * whenever the cached list could be stale (admin link/unlink during a
     * session, app regaining focus, opening the show-switcher modal).
     *
     * Errors are propagated to the caller so they can decide whether to
     * surface a toast or fall back to the cached list — the cache is left
     * untouched on failure.
     */
    public async refreshAvailableShows(): Promise<Show[]> {
        const shows = await this.getMyShows();
        this.authStateService.setAvailableShows(shows);
        return shows;
    }

    /**
     * Sets the active show for the current user.
     */
    public async setActiveShow(showId: number): Promise<void> {
        try {
            await this.put('users/me/active-show', { showId });
        } catch (error) {
            console.error('[ApiService] setActiveShow() error:', error);
            throw error;
        }
    }

    // ============ Profile Image Methods ============

    /**
     * Uploads a profile image for a user.
     * Returns the URL of the uploaded image.
     */
    public async uploadProfileImage(userId: number, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);
        const result = await this.postRequest(`users/${userId}/profile-image`, formData);
        return result?.data || '';
    }

    /**
     * Deletes the profile image for a user.
     */
    public async deleteProfileImage(userId: number): Promise<void> {
        await this.delete(`users/${userId}/profile-image`);
    }

    /**
     * Performs a POST request and returns the response as a Blob.
     * Useful for downloading files like PDFs.
     */
    public async postBlob(endpoint: string, data: any): Promise<Blob> {
        try {
            const url = this.buildUrl(endpoint);
            return await this.http.post(url, data, {
                responseType: 'blob'
            }).toPromise();
        } catch (error) {
            console.error('[ApiService] postBlob() - error', error);
            throw error;
        }
    }
}
